/*!
 * Bloom Tracker, assets/bw-tracker.js
 * Blossom Wings customer-facing period tracker (Task 8).
 * Single IIFE, no dependencies, no external requests.
 *
 * Modes:
 *   local , logged out, or logged in but pre-consent, or API unreachable
 *            (the app proxy /apps/bw-tracker is not live yet, so this is
 *            the mode every visitor will be in until Task 7 ships).
 *   server, logged in + consented + API reachable.
 *
 * localStorage schema (key "bw_tracker_v1"):
 *   { cycles: [{period_start:'YYYY-MM-DD', period_end:'YYYY-MM-DD'|null}],
 *     symptoms: {'YYYY-MM-DD': {cramps,flow,mood,headache,bloating}},
 *     settings: {locale:'en'|'ar'} }
 */
(function () {
  'use strict';

  var ROOT = document.getElementById('bw-tracker');
  if (!ROOT) return;

  var STORE_KEY = 'bw_tracker_v1';
  var PROXY_BASE = ROOT.getAttribute('data-proxy') || '/apps/bw-tracker';
  var LOGGED_IN = ROOT.getAttribute('data-logged-in') === '1';
  var LOCALE = ROOT.getAttribute('data-locale') === 'ar' ? 'ar' : 'en';
  var LOGIN_URL = ROOT.getAttribute('data-login-url') || '/account/login';

  // Order fixed, symptom sheet groups, chart dots, and count rows all
  // iterate this. Keys match the JSONB keys stored via POST /symptoms.
  var SYMPTOM_KEYS = ['cramps', 'backache', 'breast_tenderness', 'bloating', 'headache', 'mood', 'fatigue', 'skin', 'cravings', 'sleep'];

  // Stage->product config, injected by sections/bw-tracker.liquid from the
  // shop.metafields.bw_tracker.stage_map JSON (admin-editable, no code
  // change to retune). Missing/invalid config = no suggestions, silently.
  var STAGE_MAP = (function () {
    try {
      var el = document.getElementById('bw-stage-map');
      return el ? (JSON.parse(el.textContent).stages || []) : [];
    } catch (e) { return []; }
  })();

  /* ------------------------------------------------------------------ *
   * Strings (EN + AR). Disclaimer + consent copy are VERBATIM from the
   * Task 7 copy pack, do not edit wording, only add new keys.
   * ------------------------------------------------------------------ */
  var STRINGS = {
    en: {
      pageTitle: 'Bloom Tracker',
      tagline: 'Track your cycle, gently.',
      nextPeriod: 'Next period',
      inDays: 'in {n} days',
      dueToday: 'Expected today',
      expectedAround: 'expected around {date}',
      todayLabel: 'Today',
      noDataYet: 'No periods logged yet. Tap a day below to log your first period.',
      logStart: 'Period started this day',
      logEnd: 'Period ended this day',
      quickStart: 'Period started today',
      quickEnd: 'Period ended',
      logSymptomsAction: 'Log symptoms',
      symptoms: 'Symptoms',
      cancel: 'Cancel',
      save: 'Save',
      close: 'Close',
      cramps: 'Cramps',
      flow: 'Flow',
      flowLight: 'Light',
      flowMedium: 'Medium',
      flowHeavy: 'Heavy',
      mood: 'Mood',
      headache: 'Headache',
      bloating: 'Bloating',
      sym_cramps: 'Cramps',
      sym_backache: 'Backache',
      sym_breast_tenderness: 'Breast tenderness',
      sym_bloating: 'Bloating',
      sym_headache: 'Headache',
      sym_mood: 'Mood swings',
      sym_fatigue: 'Fatigue',
      sym_skin: 'Skin / breakouts',
      sym_cravings: 'Cravings',
      sym_sleep: 'Poor sleep',
      groupBody: 'Body',
      groupMood: 'Mood & energy',
      groupSkin: 'Skin & sleep',
      tabCalendar: 'Calendar',
      tabMyCycle: 'My Cycle',
      myCycleEmpty: 'Log your first period to see your cycle here.',
      currentCycle: 'Current cycle',
      cycleStats: '{len}-day cycle · {period}-day period',
      legendPeriod: 'Period',
      legendPredicted: 'Predicted',
      legendSymptoms: 'Symptoms logged',
      countsTitle: 'This cycle',
      countLogged: 'logged {d}',
      countNone: 'not logged this cycle',
      countPrev: 'last cycle: {d}',
      dayOne: '1 day',
      dayMany: '{n} days',
      historyTitle: 'Full history',
      historyMore: 'Show all',
      historyMetaFull: '{len}-day cycle · period: {p}',
      historyMetaPeriod: 'period: {p}',
      suggestShopCta: 'Shop',
      suggestQuizCta: 'Take the quiz',
      suggestExtra: 'comfort panties',
      settings: 'Settings',
      cycleLength: 'Average cycle length (days)',
      reminders: 'Email reminders',
      waReminders: 'WhatsApp reminders',
      waSoon: 'Starting soon',
      waSave: 'Save number',
      waInvalid: 'Please enter a valid phone number.',
      waMicro: 'WhatsApp reminders are starting soon. Your number is stored securely in Saudi Arabia and used only for these reminders. Turning this off deletes it.',
      downloadData: 'Download my data',
      deleteData: 'Delete my data',
      deleteConfirm: 'This permanently deletes all your tracked dates and symptoms. This cannot be undone. Are you sure?',
      deleteConfirmFinal: 'Last chance: delete everything permanently?',
      loginBanner: 'Log in to save your cycle across your devices.',
      loginCta: 'Log in',
      connectCta: 'Save my data to my account',
      consentTitle: 'Save your data securely',
      consentBody: 'To save your cycle across devices we store the dates and symptoms you log, linked to your Blossom Wings account, on servers inside Saudi Arabia. If you turn on reminders, only your email and the reminder date are shared with our email service. You can download or permanently delete your data anytime from Settings. By continuing you agree to this.',
      consentAccept: 'I agree, save my data',
      consentReminders: 'Also send me email reminders before my period',
      consentCancel: 'Not now',
      disclaimer: 'Estimates only, based on the dates you log. This is not medical advice. For any health concern, please speak to a doctor.',
      toastError: 'Couldn’t reach the server, saved on this device only.',
      toastNoOpenCycle: 'No period in progress to end.',
      toastDeleted: 'Your data has been deleted.',
      toastSettingsSaved: 'Settings saved.',
      toastSaved: 'Saved.',
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      dayNames: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      bloomName: 'Bloom',
      bloomAria: 'A message from Bloom'
    },
    ar: {
      pageTitle: 'متتبعة الدورة',
      tagline: 'تابعي دورتك برفق.',
      nextPeriod: 'الدورة القادمة',
      inDays: 'خلال {n} يوم',
      dueToday: 'متوقعة اليوم',
      expectedAround: 'متوقعة حوالي {date}',
      todayLabel: 'اليوم',
      noDataYet: 'لا توجد دورات مسجلة بعد. اضغطي على يوم في الأسفل لتسجيل أول دورة.',
      logStart: 'بدأت الدورة هذا اليوم',
      logEnd: 'انتهت الدورة هذا اليوم',
      quickStart: 'بدأت دورتي اليوم',
      quickEnd: 'انتهت دورتي',
      logSymptomsAction: 'تسجيل الأعراض',
      symptoms: 'الأعراض',
      cancel: 'إلغاء',
      save: 'حفظ',
      close: 'إغلاق',
      cramps: 'تقلصات',
      flow: 'التدفق',
      flowLight: 'خفيف',
      flowMedium: 'متوسط',
      flowHeavy: 'غزير',
      mood: 'المزاج',
      headache: 'صداع',
      bloating: 'انتفاخ',
      sym_cramps: 'تقلصات',
      sym_backache: 'ألم الظهر',
      sym_breast_tenderness: 'ألم الثدي',
      sym_bloating: 'انتفاخ',
      sym_headache: 'صداع',
      sym_mood: 'تقلب المزاج',
      sym_fatigue: 'إرهاق',
      sym_skin: 'البشرة / حبوب',
      sym_cravings: 'رغبة شديدة بالأكل',
      sym_sleep: 'نوم متقطع',
      groupBody: 'الجسم',
      groupMood: 'المزاج والطاقة',
      groupSkin: 'البشرة والنوم',
      tabCalendar: 'التقويم',
      tabMyCycle: 'دورتي',
      myCycleEmpty: 'سجلي أول دورة لك لعرض دورتك هنا.',
      currentCycle: 'الدورة الحالية',
      cycleStats: 'دورة {len} يوم · نزول {period} أيام',
      legendPeriod: 'الدورة',
      legendPredicted: 'متوقع',
      legendSymptoms: 'أعراض مسجلة',
      countsTitle: 'هذه الدورة',
      countLogged: 'مسجلة {d}',
      countNone: 'غير مسجلة هذه الدورة',
      countPrev: 'الدورة السابقة: {d}',
      dayOne: 'يوم واحد',
      dayMany: '{n} أيام',
      historyTitle: 'السجل الكامل',
      historyMore: 'عرض الكل',
      historyMetaFull: 'دورة {len} يوم · نزول: {p}',
      historyMetaPeriod: 'نزول: {p}',
      suggestShopCta: 'تسوقي',
      suggestQuizCta: 'ابدئي الاختبار',
      suggestExtra: 'الملابس الداخلية المريحة',
      settings: 'الإعدادات',
      cycleLength: 'متوسط طول الدورة (أيام)',
      reminders: 'تذكيرات بالبريد الإلكتروني',
      waReminders: 'تذكيرات واتساب',
      waSoon: 'قريبا',
      waSave: 'حفظ الرقم',
      waInvalid: 'يرجى إدخال رقم هاتف صحيح.',
      waMicro: 'تذكيرات واتساب ستبدأ قريبا. نحفظ رقمك بأمان داخل السعودية ونستخدمه فقط لهذه التذكيرات. وعند إيقاف هذه الميزة يحذف الرقم.',
      downloadData: 'تنزيل بياناتي',
      deleteData: 'حذف بياناتي',
      deleteConfirm: 'سيؤدي هذا إلى حذف جميع التواريخ والأعراض المسجلة نهائيا. لا يمكن التراجع عن هذا الإجراء. هل أنت متأكدة؟',
      deleteConfirmFinal: 'الفرصة الأخيرة: هل تريدين حذف كل شيء نهائيا؟',
      loginBanner: 'سجلي الدخول لحفظ دورتك عبر جميع أجهزتك.',
      loginCta: 'تسجيل الدخول',
      connectCta: 'احفظي بياناتي في حسابي',
      consentTitle: 'احفظي بياناتك بأمان',
      consentBody: 'لحفظ دورتك على جميع أجهزتك، نخزن التواريخ والأعراض التي تسجلينها مرتبطة بحسابك في بلوسوم وينجز على خوادم داخل السعودية. عند تفعيل التذكيرات، تتم مشاركة بريدك الإلكتروني وتاريخ التذكير فقط مع خدمة البريد لدينا. يمكنك تنزيل بياناتك أو حذفها نهائيا في أي وقت من الإعدادات. بمتابعتك فأنت توافقين على ذلك.',
      consentAccept: 'أوافق، احفظي بياناتي',
      consentReminders: 'أرسلوا لي أيضا تذكيرات بالبريد الإلكتروني قبل موعد دورتي',
      consentCancel: 'ليس الآن',
      disclaimer: 'تقديرات فقط بناء على التواريخ التي سجلتها، وليست نصيحة طبية. لأي قلق صحي يرجى استشارة الطبيبة.',
      toastError: 'تعذر الوصول إلى الخادم، تم الحفظ على هذا الجهاز فقط.',
      toastNoOpenCycle: 'لا توجد دورة قيد التقدم لإنهائها.',
      toastDeleted: 'تم حذف بياناتك.',
      toastSettingsSaved: 'تم حفظ الإعدادات.',
      toastSaved: 'تم الحفظ.',
      monthNames: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
      dayNames: ['إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت', 'أحد'],
      prevMonth: 'الشهر السابق',
      nextMonth: 'الشهر التالي',
      bloomName: 'بلوم',
      bloomAria: 'رسالة من بلوم'
    }
  };
  var S = STRINGS[LOCALE];

  function t(key, vars) {
    var str = S[key] != null ? S[key] : key;
    if (vars) {
      for (var k in vars) {
        if (Object.prototype.hasOwnProperty.call(vars, k)) {
          str = str.replace('{' + k + '}', vars[k]);
        }
      }
    }
    return str;
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // 'YYYY-MM-DD' -> 'Sep 4' (EN) / '4 سبتمبر' (AR). Raw ISO dates read as
  // machine output, every user-facing date goes through this.
  function fmtHuman(dateStr) {
    if (!dateStr || dateStr.length < 10) return dateStr || '';
    var m = parseInt(dateStr.slice(5, 7), 10) - 1;
    var d = parseInt(dateStr.slice(8, 10), 10);
    var month = S.monthNames[m] || '';
    return LOCALE === 'ar' ? d + ' ' + month : month.slice(0, 3) + ' ' + d;
  }

  // Pluralized day count: EN '1 day'/'3 days'; AR proper forms.
  function pluralDays(n) {
    if (LOCALE === 'ar') {
      if (n === 0) return '0 أيام';
      if (n === 1) return t('dayOne');
      if (n === 2) return 'يومان';
      if (n >= 3 && n <= 10) return t('dayMany', { n: n });
      return n + ' يوما';
    }
    return n === 1 ? t('dayOne') : t('dayMany', { n: n });
  }

  /* ------------------------------------------------------------------ *
   * Prediction engine, byte-for-byte port of src/predictions.js
   * (computePredictions). Same constants: cycle diffs 15-60, cycle len
   * clamp 21-45, defaults 28/5, fertile window = next-18 .. next-13.
   * ------------------------------------------------------------------ */
  var DAY = 86400000;
  function toDate(s) { return new Date(s + 'T00:00:00Z'); }
  function fmt(d) { return d.toISOString().slice(0, 10); }
  function addDays(d, n) { return new Date(d.getTime() + n * DAY); }

  function predict(cycles, opts) {
    opts = opts || {};
    var avgCycleOverride = opts.avgCycleOverride || null;
    var today = opts.today || null;

    var starts = cycles.map(function (c) { return toDate(c.period_start); })
      .sort(function (a, b) { return a - b; });
    var diffs = [];
    for (var i = 1; i < starts.length; i++) diffs.push(Math.round((starts[i] - starts[i - 1]) / DAY));
    var recent = diffs.filter(function (d) { return d >= 15 && d <= 60; }).slice(-6);
    var avgCycle = avgCycleOverride ||
      (recent.length ? Math.round(recent.reduce(function (a, b) { return a + b; }, 0) / recent.length) : 28);
    avgCycle = Math.min(45, Math.max(21, avgCycle));

    var lens = cycles.slice()
      .sort(function (a, b) { return a.period_start < b.period_start ? -1 : 1; })
      .filter(function (c) { return c.period_end; })
      .map(function (c) { return Math.round((toDate(c.period_end) - toDate(c.period_start)) / DAY) + 1; })
      .filter(function (l) { return l >= 1 && l <= 10; })
      .slice(-6);
    var avgPeriod = lens.length ? Math.round(lens.reduce(function (a, b) { return a + b; }, 0) / lens.length) : 5;

    if (!starts.length) {
      return { avgCycle: avgCycle, avgPeriod: avgPeriod, nextStart: null, daysUntil: null, fertileStart: null, fertileEnd: null };
    }
    var now = today ? toDate(today) : toDate(fmt(new Date()));
    var next = addDays(starts[starts.length - 1], avgCycle);
    while (next < now) next = addDays(next, avgCycle);
    return {
      avgCycle: avgCycle,
      avgPeriod: avgPeriod,
      nextStart: fmt(next),
      daysUntil: Math.round((next - now) / DAY),
      fertileStart: fmt(addDays(next, -18)),
      fertileEnd: fmt(addDays(next, -13))
    };
  }

  /* ------------------------------------------------------------------ *
   * localStorage store
   * ------------------------------------------------------------------ */
  function loadLocal() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return { cycles: [], symptoms: {}, settings: { locale: LOCALE } };
      var parsed = JSON.parse(raw);
      return {
        cycles: Array.isArray(parsed.cycles) ? parsed.cycles : [],
        symptoms: (parsed.symptoms && typeof parsed.symptoms === 'object') ? parsed.symptoms : {},
        settings: parsed.settings || { locale: LOCALE }
      };
    } catch (e) {
      return { cycles: [], symptoms: {}, settings: { locale: LOCALE } };
    }
  }
  function saveLocal(data) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch (e) { /* storage unavailable, degrade silently */ }
  }
  function wipeLocal() {
    try { localStorage.removeItem(STORE_KEY); } catch (e) { /* ignore */ }
  }

  /* ------------------------------------------------------------------ *
   * API helper, same-origin fetch against the app proxy. The proxy is
   * not live yet, so every call is expected to fail right now; callers
   * must degrade to local mode + a non-blocking toast on any failure.
   * ------------------------------------------------------------------ */
  function api(path, opts) {
    opts = opts || {};
    var headers = { 'Content-Type': 'application/json' };
    if (opts.headers) { for (var k in opts.headers) headers[k] = opts.headers[k]; }
    var fetchOpts = { credentials: 'same-origin', method: opts.method || 'GET', headers: headers };
    if (opts.body) fetchOpts.body = opts.body;
    return fetch(PROXY_BASE + path, fetchOpts).then(function (res) {
      return res.text().then(function (text) {
        var body = {};
        try { body = text ? JSON.parse(text) : {}; } catch (e) { /* non-JSON response */ }
        if (!res.ok) {
          var err = new Error((body && body.error) || ('http_' + res.status));
          err.status = res.status;
          err.body = body;
          // Stale tab whose consent was revoked server-side: drop back to
          // local mode and re-open the consent sheet instead of a generic
          // error toast. Callers check err.handled to skip their own toast.
          if (res.status === 409 && body && body.error === 'consent_required') {
            state.mode = 'local';
            state.server = null;
            openSheet({ type: 'consent' });
            err.handled = true;
          }
          throw err;
        }
        return body;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */
  var today = new Date();
  var state = {
    mode: 'local',
    loggedIn: LOGGED_IN,
    local: loadLocal(),
    server: null, // {user, cycles, symptoms, predictions}
    viewYear: today.getFullYear(),
    viewMonth: today.getMonth(), // 0-11
    view: 'calendar', // 'calendar' | 'mycycle'
    sheet: null // current overlay descriptor
  };
  // In-flight guards for actions that must not double-submit while their
  // request is pending (consent accept, save cycle length, symptoms save).
  var inFlight = { consent: false, cycleLength: false, symptoms: false, wa: false };

  function localTodayStr() {
    var d = new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function currentCycles() {
    return state.mode === 'server' && state.server ? state.server.cycles : state.local.cycles;
  }
  // Server /me sends symptoms as an array of {date, symptoms} rows; local
  // cache keeps a {date: symptoms} map. Normalize to the map shape.
  function currentSymptoms() {
    if (state.mode === 'server' && state.server) {
      var m = {};
      var rows = state.server.symptoms || [];
      for (var i = 0; i < rows.length; i++) m[rows[i].date] = rows[i].symptoms;
      return m;
    }
    return state.local.symptoms;
  }
  function currentPredictions() {
    if (state.mode === 'server' && state.server && state.server.predictions) return state.server.predictions;
    return predict(currentCycles());
  }

  /* ------------------------------------------------------------------ *
   * Toast
   * ------------------------------------------------------------------ */
  var toastTimer = null;
  function ensureToast() {
    if (document.getElementById('bw-toast')) return;
    var el = document.createElement('div');
    el.id = 'bw-toast';
    el.className = 'bw-t-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  function toast(msg) {
    ensureToast();
    var el = document.getElementById('bw-toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 3200);
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot() {
    if (!state.loggedIn) {
      state.mode = 'local';
      render();
      return;
    }
    api('/me').then(function (data) {
      if (data && data.user) {
        state.mode = 'server';
        state.server = data;
      } else {
        state.mode = 'local'; // user===null: reachable, but no consent yet
      }
      render();
    }).catch(function () {
      // Proxy not live (or any network/HTTP failure): stay on local cache.
      state.mode = 'local';
      render();
    });
    render(); // paint local-cache UI immediately while /me resolves
  }

  function refreshServer() {
    return api('/me').then(function (data) {
      if (data && data.user) {
        state.mode = 'server';
        state.server = data;
      } else {
        state.mode = 'local';
        state.server = null;
      }
      render();
    }).catch(function (err) {
      if (err && err.handled) { return; } // api() already switched to local + opened consent sheet
      if (err && (err.status === 401)) {
        state.mode = 'local';
        state.server = null;
      }
      toast(t('toastError'));
      render();
    });
  }

  /* ------------------------------------------------------------------ *
   * Mutations
   * ------------------------------------------------------------------ */
  function addCycleStart(dateStr) {
    if (state.mode === 'server') {
      // POST /cycles upserts on (customer, period_start): posting without
      // period_end would null out an already-ended cycle sharing this same
      // start date, so look it up first and carry its period_end forward.
      var list = currentCycles();
      var existing = null;
      for (var i = 0; i < list.length; i++) {
        if (list[i].period_start === dateStr) { existing = list[i]; break; }
      }
      var payload = { period_start: dateStr };
      if (existing && existing.period_end) payload.period_end = existing.period_end;
      return api('/cycles', { method: 'POST', body: JSON.stringify(payload) })
        .then(function () { return refreshServer().then(function () { toast(t('toastSaved')); }); })
        .catch(function (err) { if (!(err && err.handled)) toast(t('toastError')); });
    }
    // Local mode: avoid pushing a second cycle for the same period_start.
    var idx = -1;
    for (var j = 0; j < state.local.cycles.length; j++) {
      if (state.local.cycles[j].period_start === dateStr) { idx = j; break; }
    }
    if (idx === -1) {
      state.local.cycles.push({ period_start: dateStr, period_end: null });
      saveLocal(state.local);
    }
    render();
    toast(t('toastSaved'));
    return Promise.resolve();
  }

  function findOpenCycleForEnd(list, dateStr) {
    var candidates = [];
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (!c.period_end && dateStr >= c.period_start) candidates.push(c);
    }
    if (!candidates.length) return null;
    candidates.sort(function (a, b) { return a.period_start < b.period_start ? 1 : -1; });
    return candidates[0];
  }

  function endCycle(dateStr) {
    var list = currentCycles();
    var target = findOpenCycleForEnd(list, dateStr);
    if (!target) { toast(t('toastNoOpenCycle')); return; }
    if (state.mode === 'server') {
      // No PATCH endpoint in the API contract, but POST /cycles upserts on
      // (customer, period_start), ON CONFLICT DO UPDATE SET period_end=$3 -
      // so re-posting the same period_start with the new period_end updates
      // the existing row atomically. No DELETE needed.
      return api('/cycles', { method: 'POST', body: JSON.stringify({ period_start: target.period_start, period_end: dateStr }) })
        .then(function () { return refreshServer().then(function () { toast(t('toastSaved')); }); })
        .catch(function (err) { if (!(err && err.handled)) toast(t('toastError')); });
    }
    target.period_end = dateStr;
    saveLocal(state.local);
    render();
    toast(t('toastSaved'));
    return Promise.resolve();
  }

  function logSymptoms(dateStr, symptoms) {
    if (state.mode === 'server') {
      return api('/symptoms', { method: 'POST', body: JSON.stringify({ date: dateStr, symptoms: symptoms }) })
        .then(function () { return refreshServer().then(function () { toast(t('toastSaved')); }); })
        .catch(function (err) { if (!(err && err.handled)) toast(t('toastError')); });
    }
    state.local.symptoms[dateStr] = symptoms;
    saveLocal(state.local);
    render();
    toast(t('toastSaved'));
    return Promise.resolve();
  }

  function submitConsent(remindersEnabled) {
    var cycles = state.local.cycles.map(function (c) {
      var o = { period_start: c.period_start };
      if (c.period_end) o.period_end = c.period_end;
      return o;
    });
    api('/consent', { method: 'POST', body: JSON.stringify({ locale: LOCALE, reminders_enabled: !!remindersEnabled, cycles: cycles }) })
      .then(function () {
        var dates = Object.keys(state.local.symptoms);
        var replay = dates.reduce(function (p, d) {
          return p.then(function () {
            return api('/symptoms', { method: 'POST', body: JSON.stringify({ date: d, symptoms: state.local.symptoms[d] }) }).catch(function () {});
          });
        }, Promise.resolve());
        return replay;
      })
      .then(function () {
        wipeLocal();
        location.reload();
      })
      .catch(function (err) {
        inFlight.consent = false;
        if (err && err.handled) return; // api() already re-opened the consent sheet
        toast(t('toastError'));
        closeSheet();
      });
  }

  function saveSetting(patch) {
    return api('/settings', { method: 'PUT', body: JSON.stringify(patch) })
      .then(function () { toast(t('toastSettingsSaved')); return refreshServer(); })
      .catch(function (err) {
        if (!(err && err.handled)) {
          // v3.1 whatsapp option: PUT /settings phone validation errors get
          // the specific message; every other failure keeps the generic toast.
          var code = err && err.body && err.body.error;
          toast(code === 'invalid_phone' || code === 'phone_required' ? t('waInvalid') : t('toastError'));
          render();
        }
      });
  }

  function downloadData() {
    api('/export').then(function (data) {
      var blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'bloom-tracker-export-' + localTodayStr() + '.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
    }).catch(function (err) { if (!(err && err.handled)) toast(t('toastError')); });
  }

  function deleteAllData() {
    if (!window.confirm(t('deleteConfirm'))) return;
    if (!window.confirm(t('deleteConfirmFinal'))) return;
    api('/me', { method: 'DELETE' }).then(function () {
      wipeLocal();
      toast(t('toastDeleted'));
      setTimeout(function () { location.reload(); }, 600);
    }).catch(function (err) { if (!(err && err.handled)) toast(t('toastError')); });
  }

  /* ------------------------------------------------------------------ *
   * Calendar helpers
   * ------------------------------------------------------------------ */
  function addDaysStr(dateStr, n) { return fmt(addDays(toDate(dateStr), n)); }

  function buildMonthGrid(year, month) {
    var firstOfMonth = new Date(year, month, 1);
    var firstWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday = 0
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var cells = [];
    for (var i = 0; i < firstWeekday; i++) cells.push(null);
    for (var d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }
  function localDateStr(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }

  function classesForDate(dateStr, cyclesList, pred, symptomsMap) {
    var cls = [];
    if (dateStr === localTodayStr()) cls.push('today');
    if (symptomsMap && symptomsMap[dateStr]) cls.push('has-symptoms');
    for (var i = 0; i < cyclesList.length; i++) {
      var c = cyclesList[i];
      var end = c.period_end || addDaysStr(c.period_start, pred.avgPeriod - 1);
      if (dateStr >= c.period_start && dateStr <= end) { cls.push('period'); break; }
    }
    if (pred.nextStart) {
      var predEnd = addDaysStr(pred.nextStart, pred.avgPeriod - 1);
      if (dateStr >= pred.nextStart && dateStr <= predEnd) cls.push('predicted');
    }
    return cls;
  }

  /* ------------------------------------------------------------------ *
   * Render
   * ------------------------------------------------------------------ */
  function render() {
    var pred = currentPredictions();
    var cyclesList = currentCycles();
    var desktop = isDesktop();

    // v3 wrapper grid (Task 1): fixed area containers inside the card
    // shell. Mobile stacks them as one column and keeps the tab swap
    // (state.view decides calendar vs My Cycle); desktop (>=900px, see
    // the v3 layout block in bw-tracker.css) arranges the same areas as
    // a dashboard grid, renders calendar and My Cycle together, ignores
    // state.view and skips the tab bar. The companion area hosts Bloom,
    // the scripted mascot card (v3 Task 3, see renderCompanion).
    // Toast + sheet overlay stay outside the grid (fixed elements).
    var html = '';
    html += '<div class="bw-t-card bw-t-grid">';
    html += '<div class="bw-t-area-companion">' + renderCompanion() + '</div>';
    html += '<div class="bw-t-area-hero">' + renderBanner(pred, cyclesList) + '</div>';
    html += '<div class="bw-t-area-cal">';
    if (!desktop) {
      html += '<div class="bw-t-tabs" role="tablist">';
      html += '<button type="button" class="bw-t-tab' + (state.view === 'calendar' ? ' is-active' : '') + '" data-action="view-calendar">' + esc(t('tabCalendar')) + '</button>';
      html += '<button type="button" class="bw-t-tab' + (state.view === 'mycycle' ? ' is-active' : '') + '" data-action="view-mycycle">' + esc(t('tabMyCycle')) + '</button>';
      html += '</div>';
    }
    if (desktop || state.view === 'calendar') {
      html += renderCalendar(pred, cyclesList);
    }
    html += '</div>';
    html += '<div class="bw-t-area-side">';
    html += renderSuggestion();
    html += renderFooter();
    html += '</div>';
    html += '<div class="bw-t-area-mycycle">';
    if (desktop || state.view === 'mycycle') {
      html += renderMyCycle();
    }
    html += '</div>';
    html += '</div>';
    html += renderSheet();
    html += renderQuickPill(cyclesList); // v3 quick log (Task 2), outside the grid

    ROOT.innerHTML = html;
    bindEvents();
    refreshQuickPillObserver(); // DOM rebuilt, re-observe the fresh hero node
  }

  /* ------------------------------------------------------------------ *
   * Stage suggestion, first matching stage wins (heavy-flow rule sits
   * first in the config). Commerce framing only, never health advice.
   * ------------------------------------------------------------------ */
  function suggestStage() {
    if (!STAGE_MAP.length) return null;
    var cycles = currentCycles(), pred = currentPredictions(), symMap = currentSymptoms();
    var todayStr = localTodayStr();
    var todayFlow = symMap[todayStr] ? symMap[todayStr].flow : null;
    var dayOfPeriod = 0;
    if (cycles.length) {
      var latest = cycles.slice().sort(function (a, b) { return a.period_start < b.period_start ? -1 : 1; })[cycles.length - 1];
      var pEnd = latest.period_end || addDaysStr(latest.period_start, pred.avgPeriod - 1);
      if (todayStr >= latest.period_start && todayStr <= pEnd)
        dayOfPeriod = Math.round((toDate(todayStr) - toDate(latest.period_start)) / DAY) + 1;
    }
    for (var i = 0; i < STAGE_MAP.length; i++) {
      var st = STAGE_MAP[i], m = st.match || {};
      if (m.today_flow) { if (todayFlow === m.today_flow) return st; continue; }
      if (m.day_of_period) { if (dayOfPeriod >= m.day_of_period[0] && dayOfPeriod <= m.day_of_period[1]) return st; continue; }
      if (m.days_until) { if (pred.daysUntil !== null && pred.daysUntil >= m.days_until[0] && pred.daysUntil <= m.days_until[1]) return st; continue; }
      return st; // empty match = catch-all
    }
    return null;
  }

  function renderSuggestion() {
    var st = suggestStage(); if (!st) return '';
    var isAr = LOCALE === 'ar';
    var cta = isAr ? st.cta_ar : st.cta_en; if (!cta) return '';
    var prefix = isAr ? '/ar' : '';
    var href = st.quiz_link ? prefix + '/pages/quiz' : (st.handle ? prefix + '/collections/' + st.handle : null);
    if (!href) return '';
    var h = '<div class="bw-t-suggest"><span class="bw-t-suggest-text">' + esc(cta) + '</span>';
    h += '<a class="bw-t-btn bw-t-btn--primary" href="' + href + '">' + esc(t(st.quiz_link ? 'suggestQuizCta' : 'suggestShopCta')) + '</a>';
    if (st.extra_handle) h += '<a class="bw-t-suggest-extra" href="' + prefix + '/collections/' + st.extra_handle + '">+ ' + esc(t('suggestExtra')) + '</a>';
    return h + '</div>';
  }

  /* ------------------------------------------------------------------ *
   * My Cycle view, timeline strips + symptom counts. Descriptive data
   * only (SFDA): dates, day counts, no interpretation.
   * ------------------------------------------------------------------ */
  function buildCycleRanges(cycles, pred) {
    var sorted = cycles.slice().sort(function (a, b) { return a.period_start < b.period_start ? -1 : 1; });
    var out = [];
    for (var i = 0; i < sorted.length; i++) {
      var c = sorted[i];
      var nextStart = (i + 1 < sorted.length) ? sorted[i + 1].period_start : null;
      var end = nextStart ? addDaysStr(nextStart, -1) : localTodayStr();
      if (end < c.period_start) end = c.period_start;
      var periodEnd = c.period_end || addDaysStr(c.period_start, pred.avgPeriod - 1);
      if (periodEnd > end) periodEnd = end;
      var len = Math.round((toDate(end) - toDate(c.period_start)) / DAY) + 1;
      out.push({ start: c.period_start, end: end, periodEnd: periodEnd, isCurrent: !nextStart, lengthDays: len });
    }
    return out.slice(-4).reverse();
  }

  function renderCycleStrips(ranges, symMap, pred) {
    var maxLen = 28;
    for (var i = 0; i < ranges.length; i++) if (ranges[i].lengthDays > maxLen) maxLen = Math.min(45, ranges[i].lengthDays);
    var h = '';
    for (var r = 0; r < ranges.length; r++) {
      var cy = ranges[r], unit = 100 / maxLen;
      var periodDays = Math.round((toDate(cy.periodEnd) - toDate(cy.start)) / DAY) + 1;
      // Taller labeled timeline: bar row (y 7-13) + separated dot row (y 19),
      // week ticks, and a today marker on the current cycle. Date labels are
      // HTML below the SVG (SVG text would stretch: preserveAspectRatio=none).
      var svg = '<svg viewBox="0 0 100 22" preserveAspectRatio="none" class="bw-t-strip-svg" aria-hidden="true">';
      for (var w = 7; w < maxLen; w += 7) {
        svg += '<line x1="' + (w * unit).toFixed(2) + '" y1="5" x2="' + (w * unit).toFixed(2) + '" y2="15" class="bw-t-strip-tick"/>';
      }
      svg += '<rect x="0" y="7" width="' + Math.min(100, cy.lengthDays * unit).toFixed(2) + '" height="6" rx="3" class="bw-t-strip-track"/>';
      svg += '<rect x="0" y="7" width="' + Math.min(100, periodDays * unit).toFixed(2) + '" height="6" rx="3" class="bw-t-strip-period"/>';
      if (cy.isCurrent && pred.nextStart && pred.nextStart > cy.start) {
        var d0 = Math.round((toDate(pred.nextStart) - toDate(cy.start)) / DAY);
        if (d0 < maxLen) svg += '<rect x="' + (d0 * unit).toFixed(2) + '" y="7" width="' + Math.min(100 - d0 * unit, pred.avgPeriod * unit).toFixed(2) + '" height="6" rx="3" class="bw-t-strip-predicted"/>';
      }
      if (cy.isCurrent) {
        var ti = Math.round((toDate(localTodayStr()) - toDate(cy.start)) / DAY);
        if (ti >= 0 && ti < maxLen) {
          svg += '<line x1="' + ((ti + 0.5) * unit).toFixed(2) + '" y1="3" x2="' + ((ti + 0.5) * unit).toFixed(2) + '" y2="17" class="bw-t-strip-today"/>';
        }
      }
      var d = cy.start, di = 0;
      while (d <= cy.end && di < maxLen) {
        var s = symMap[d];
        if (s) {
          var has = s.flow ? true : false;
          for (var k = 0; k < SYMPTOM_KEYS.length && !has; k++) if (s[SYMPTOM_KEYS[k]]) has = true;
          if (has) svg += '<circle cx="' + ((di + 0.5) * unit).toFixed(2) + '" cy="19" r="1.5" class="bw-t-strip-dot"/>';
        }
        d = addDaysStr(d, 1); di++;
      }
      svg += '</svg>';
      var label = cy.isCurrent ? t('currentCycle') : fmtHuman(cy.start) + ' - ' + fmtHuman(cy.end);
      h += '<div class="bw-t-strip"><div class="bw-t-strip-meta"><span class="bw-t-strip-name">' + esc(label) + '</span><span>' +
        esc(t('cycleStats', { len: cy.lengthDays, period: periodDays })) + '</span></div>' + svg +
        '<div class="bw-t-strip-dates"><span>' + esc(fmtHuman(cy.start)) + '</span><span>' +
        esc(cy.isCurrent ? t('todayLabel') : fmtHuman(cy.end)) + '</span></div></div>';
    }
    return h;
  }

  function countSymptoms(range, symMap) {
    var counts = {}, d = range.start, guard = 0;
    while (d <= range.end && guard < 60) {
      var s = symMap[d];
      if (s) for (var i = 0; i < SYMPTOM_KEYS.length; i++) if (s[SYMPTOM_KEYS[i]]) counts[SYMPTOM_KEYS[i]] = (counts[SYMPTOM_KEYS[i]] || 0) + 1;
      d = addDaysStr(d, 1); guard++;
    }
    return counts;
  }

  function renderSymptomCounts(ranges, symMap) {
    if (!ranges.length) return '';
    var cur = countSymptoms(ranges[0], symMap);
    var prev = ranges[1] ? countSymptoms(ranges[1], symMap) : null;
    var rows = '';
    for (var i = 0; i < SYMPTOM_KEYS.length; i++) {
      var k = SYMPTOM_KEYS[i];
      if (!cur[k] && !(prev && prev[k])) continue;
      var curStr = (cur[k] || 0) === 0 ? t('countNone') : t('countLogged', { d: pluralDays(cur[k]) });
      rows += '<div class="bw-t-count-row"><span class="bw-t-count-name">' + esc(t('sym_' + k)) + '</span><span class="bw-t-count-val">' +
        esc(curStr) + (prev ? '<em>' + esc(t('countPrev', { d: pluralDays(prev[k] || 0) })) + '</em>' : '') + '</span></div>';
    }
    if (!rows) return '';
    return '<h2 class="bw-t-counts-title">' + esc(t('countsTitle')) + '</h2><div class="bw-t-counts">' + rows + '</div>';
  }

  // v3.2 cycle history: full descriptive list of completed cycles below the
  // strips + counts. Dates and day counts only, no interpretation (SFDA).
  // Expanded flag lives here (not in the DOM) so re-renders keep the state.
  var historyOpen = false;

  function renderCycleHistory(cycles) {
    var sorted = cycles.slice().sort(function (a, b) { return a.period_start < b.period_start ? -1 : 1; });
    var completed = [];
    for (var i = 0; i < sorted.length; i++) {
      if (sorted[i].period_start && sorted[i].period_end) {
        // Next chronological start (any cycle, open one included) gives the
        // true cycle length; null for the newest cycle overall.
        completed.push({ c: sorted[i], nextStart: (i + 1 < sorted.length) ? sorted[i + 1].period_start : null });
      }
    }
    if (!completed.length) return '';
    // The strips already show the last 4 cycles; only add this section when
    // at least one completed cycle falls outside that window.
    var stripStarts = {};
    var shown = sorted.slice(-4);
    for (var s = 0; s < shown.length; s++) stripStarts[shown[s].period_start] = true;
    var beyond = false;
    for (var b = 0; b < completed.length; b++) if (!stripStarts[completed[b].c.period_start]) beyond = true;
    if (!beyond) return '';
    completed.reverse(); // newest first
    var rows = '';
    for (var r = 0; r < completed.length; r++) {
      var cy = completed[r].c;
      var periodLen = Math.round((toDate(cy.period_end) - toDate(cy.period_start)) / DAY) + 1;
      var meta;
      if (completed[r].nextStart) {
        var cycleLen = Math.round((toDate(completed[r].nextStart) - toDate(cy.period_start)) / DAY);
        meta = t('historyMetaFull', { len: cycleLen, p: pluralDays(periodLen) });
      } else {
        meta = t('historyMetaPeriod', { p: pluralDays(periodLen) });
      }
      rows += '<div class="bw-t-hist-row"><span class="bw-t-hist-range">' +
        esc(fmtHuman(cy.period_start) + ' - ' + fmtHuman(cy.period_end)) + '</span>' +
        '<span class="bw-t-hist-meta">' + esc(meta) + '</span></div>';
    }
    var collapsible = completed.length > 6;
    var h = '<h2 class="bw-t-counts-title bw-t-hist-title">' + esc(t('historyTitle')) + '</h2>';
    h += '<div class="bw-t-hist' + (!collapsible || historyOpen ? ' is-open' : '') + '">' + rows + '</div>';
    if (collapsible && !historyOpen) {
      h += '<button type="button" class="bw-t-btn bw-t-btn--ghost bw-t-hist-more" data-action="history-more">' + esc(t('historyMore')) + '</button>';
    }
    return h;
  }

  function renderMyCycle() {
    var cycles = currentCycles();
    if (!cycles.length) return '<section class="bw-t-mycycle"><p class="bw-t-empty">' + esc(t('myCycleEmpty')) + '</p></section>';
    var pred = currentPredictions();
    var symMap = currentSymptoms();
    var ranges = buildCycleRanges(cycles, pred);
    var h = '<section class="bw-t-mycycle">';
    h += renderCycleStrips(ranges, symMap, pred);
    h += '<div class="bw-t-legend">' +
      '<span><i class="bw-t-legend-swatch bw-t-legend-swatch--period"></i>' + esc(t('legendPeriod')) + '</span>' +
      '<span><i class="bw-t-legend-swatch bw-t-legend-swatch--predicted"></i>' + esc(t('legendPredicted')) + '</span>' +
      '<span><i class="bw-t-legend-swatch bw-t-legend-swatch--dot"></i>' + esc(t('legendSymptoms')) + '</span></div>';
    h += renderSymptomCounts(ranges, symMap);
    h += renderCycleHistory(cycles);
    h += '</section>';
    return h;
  }

  function renderBanner(pred, cyclesList) {
    var h = '<header class="bw-t-banner">';
    h += '<h1 class="bw-t-title">' + esc(t('pageTitle')) + '</h1>';
    h += '<p class="bw-t-tagline">' + esc(t('tagline')) + '</p>';

    if (!cyclesList.length) {
      h += '<p class="bw-t-empty">' + esc(t('noDataYet')) + '</p>';
    } else if (pred.nextStart) {
      // Hero = the human sentence ("in 27 days"), not the machine date.
      h += '<div class="bw-t-next">';
      h += '<span class="bw-t-next-label">' + esc(t('nextPeriod')) + '</span>';
      h += '<span class="bw-t-next-days">' + esc(pred.daysUntil === 0 ? t('dueToday') : t('inDays', { n: pred.daysUntil })) + '</span>';
      if (pred.daysUntil !== 0) {
        h += '<span class="bw-t-next-date">' + esc(t('expectedAround', { date: fmtHuman(pred.nextStart) })) + '</span>';
      }
      h += '</div>';
    }

    if (!state.loggedIn) {
      h += '<div class="bw-t-banner-alert">';
      h += '<span>' + esc(t('loginBanner')) + '</span>';
      // routes.account_login_url (/customer_authentication/redirect) always
      // lands on the hosted order-index page and ignores return params. The
      // documented storefront-return path for new customer accounts is
      // /customer_authentication/login?return_to=<relative path>
      // (shopify.dev/docs/storefronts/themes/sign-in). Return to the current
      // path so /ar stays /ar.
      h += '<a class="bw-t-btn bw-t-btn--ghost" href="/customer_authentication/login?return_to=' + encodeURIComponent(window.location.pathname) + '">' + esc(t('loginCta')) + '</a>';
      h += '</div>';
    } else if (state.mode === 'local') {
      h += '<div class="bw-t-banner-alert">';
      h += '<button type="button" class="bw-t-btn bw-t-btn--primary" data-action="open-consent">' + esc(t('connectCta')) + '</button>';
      h += '</div>';
    }

    // v3 quick log (Task 2): one-tap action row. Open-cycle detection is
    // the exact lookup endCycle() uses (findOpenCycleForEnd against today),
    // so button state and mutation behavior can never disagree.
    var openCycle = findOpenCycleForEnd(cyclesList, localTodayStr());
    h += '<div class="bw-t-quick-row">';
    if (openCycle) {
      h += '<button type="button" class="bw-t-btn bw-t-btn--primary" data-action="quick-end">' + esc(t('quickEnd')) + '</button>';
    } else {
      h += '<button type="button" class="bw-t-btn bw-t-btn--primary" data-action="quick-start">' + esc(t('quickStart')) + '</button>';
    }
    h += '</div>';

    h += '<p class="bw-t-disclaimer">' + esc(t('disclaimer')) + '</p>';
    h += '</header>';
    return h;
  }

  // v3 quick log (Task 2): fixed bottom pill mirroring the hero action.
  // Rendered outside .bw-t-grid next to toast/overlay; scroll visibility
  // is driven by the IntersectionObserver on the hero (see
  // refreshQuickPillObserver), sheet-open hiding is decided here at
  // render time via the is-hidden class.
  function renderQuickPill(cyclesList) {
    var openCycle = findOpenCycleForEnd(cyclesList, localTodayStr());
    var action = openCycle ? 'quick-end' : 'quick-start';
    var label = openCycle ? t('quickEnd') : t('quickStart');
    var cls = 'bw-t-btn bw-t-btn--primary bw-t-quickpill' + (state.sheet ? ' is-hidden' : '');
    return '<button type="button" class="' + cls + '" data-action="' + action + '">' + esc(label) + '</button>';
  }

  /* ------------------------------------------------------------------ *
   * v3 bloom companion (Task 3): "Bloom", the scripted mascot card in
   * the companion grid area. Pure local logic: a fixed message library,
   * a state-driven bucket picker, and a localStorage rotation memory so
   * lines don't repeat until a bucket is exhausted. Zero network. Copy
   * is emotional comfort only, never advice and never interpretation.
   * ------------------------------------------------------------------ */
  var BLOOM_SEEN_KEY = 'bw_bloom_seen';

  var BLOOM_LIB = {
    welcome: [
      { en: 'Hi, I\'m Bloom! Log your first period and I\'ll keep you company 🌸', ar: 'اهلين، انا بلوم! سجلي اول دورة لك وانا بكون معك 🌸' },
      { en: 'Welcome in! This little corner is all yours.', ar: 'يا هلا فيك! هذي الزاوية الصغيرة كلها لك.' },
      { en: 'I\'m Bloom, your cycle buddy. Whenever you feel ready, tap a day below ✨', ar: 'انا بلوم، رفيقة دورتك. متى ما حسيتي انك جاهزة اضغطي على يوم تحت ✨' },
      { en: 'New here? No rush at all. We take it one day at a time.', ar: 'جديدة هنا؟ ولا يهمك، ناخذها يوم بيوم وبدون استعجال.' },
      { en: 'So happy you\'re here. Make yourself comfy 💗', ar: 'سعيدة بوجودك هنا، خذي راحتك 💗' },
      { en: 'Once you log a period, I start learning your rhythm.', ar: 'اول ما تسجلين دورة، ابدا اتعرف على ايقاع ايامك.' },
      { en: 'Think of me as the friend who always remembers your dates.', ar: 'اعتبريني الصديقة اللي دايم تتذكر تواريخك.' },
      { en: 'A warm hello from me to you. This space stays calm, always ☁️', ar: 'تحية دافية مني لك، هذي المساحة هادية دايم ☁️' }
    ],
    period_early: [
      { en: 'The first days ask for extra gentleness. You\'ve got this 💗', ar: 'اول الايام تحتاج لطف زيادة، كوني حنونة على نفسك 💗' },
      { en: 'Warm drink, soft blanket, zero guilt. That\'s the whole plan ☁️', ar: 'مشروب دافي وبطانية ناعمة وبدون اي تانيب ضمير، هذي كل الخطة ☁️' },
      { en: 'Go slow today. Slow still counts as moving.', ar: 'خذيها بهدوء اليوم، الهدوء برضو يعتبر تقدم.' },
      { en: 'It\'s okay to cancel plans and curl up. I\'d do the same.', ar: 'عادي جدا تلغين مشاويرك وتتكومين ببطانيتك، انا بعد كنت بسوي كذا.' },
      { en: 'Deep breath in, slow breath out. I\'m right here with you 🌸', ar: 'خذي نفس عميق وطلعيه بهدوء، انا هنا معك 🌸' },
      { en: 'Today, comfort comes first. Everything else can wait.', ar: 'اليوم الراحة اول شي، وكل شي ثاني يقدر ينتظر.' },
      { en: 'Be as kind to yourself as you\'d be to your best friend.', ar: 'عاملي نفسك بنفس اللطف اللي تعاملين فيه اعز صديقاتك.' },
      { en: 'A cozy day counts as a full day. Promise 🫖', ar: 'اليوم الهادي المريح يعتبر يوم كامل، صدقيني 🫖' }
    ],
    period_late: [
      { en: 'You\'re through the toughest part. Almost there 💗', ar: 'عديتي اصعب جزء، باقي شوي وتخلصين 💗' },
      { en: 'Look at you, still glowing through it all ✨', ar: 'شوفي نفسك، لسه متالقة رغم كل شي ✨' },
      { en: 'The finish line is close. Keep taking it easy.', ar: 'خط النهاية قرب، كملي على راحتك.' },
      { en: 'From here, each day feels a little lighter.', ar: 'من هنا ورايح كل يوم اخف من اللي قبله.' },
      { en: 'You\'ve been so patient with yourself. That matters.', ar: 'صبرتي على نفسك كثير هالايام، وهذا الشي له قيمة.' },
      { en: 'Nearly done! A little more rest and you\'re back to full sparkle 🌸', ar: 'قربتي تخلصين! شوية راحة بعد وترجعين لبريقك 🌸' },
      { en: 'Proud of you for riding this one out.', ar: 'فخورة فيك انك عديتي هذي الايام.' },
      { en: 'Home stretch. Spoil yourself with something small and lovely 🫖', ar: 'اخر شوط، دللي نفسك بشي صغير وحلو 🫖' }
    ],
    pre_period: [
      { en: 'Your period may arrive in the next few days. Cozy season loading 🌸', ar: 'دورتك ممكن توصل خلال الايام الجاية، جهزي اجواء الدفا والراحة 🌸' },
      { en: 'A little heads-up from me: it might be soon. Plan a gentle week.', ar: 'تنبيه صغير مني: يمكن قربت، خططي لاسبوع هادي.' },
      { en: 'These next few days, give yourself a little extra grace 💗', ar: 'الايام الجاية عطي نفسك لطف زيادة 💗' },
      { en: 'It could be any day now. Your comfiest clothes are calling ☁️', ar: 'ممكن تبدا اي يوم، ملابسك المريحة تناديك ☁️' },
      { en: 'Soon-ish! A good time to clear a little space in your week.', ar: 'على الابواب تقريبا! وقت مناسب تفضين شوي من جدولك.' },
      { en: 'Getting close. Maybe tonight is a favorite show kind of night ✨', ar: 'قربت، يمكن الليلة مناسبة لمسلسلك المفضل ✨' },
      { en: 'I\'ll be right here when it starts. No surprises between us 💗', ar: 'انا هنا اول ما تبدا، ما بيننا مفاجات 💗' },
      { en: 'Almost that time. Gentle days ahead, and we do them together.', ar: 'قرب وقتها، ايام هادية جاية وبنعديها مع بعض.' }
    ],
    rest: [
      { en: 'Just popping in to say you\'re doing great ✨', ar: 'مريت بس عشان اقولك انك رائعة ✨' },
      { en: 'An ordinary day is a lovely thing. Enjoy it.', ar: 'اليوم العادي شي جميل، استمتعي فيه.' },
      { en: 'Little nudge from me: a glass of water and a stretch feel lovely 🫖', ar: 'تذكير صغير مني: كوب ماء وتمدد خفيف احساسهم حلو 🫖' },
      { en: 'You showed up today. That counts for a lot.', ar: 'انتي حاضرة اليوم، وهذا الشي يعني كثير.' },
      { en: 'Nothing to report, and honestly? I love that for us ☁️', ar: 'ما في شي جديد، وبصراحة يعجبني هذا الهدوء ☁️' },
      { en: 'Sending you a tiny cheer from your quiet corner 🌸', ar: 'ارسل لك تشجيع صغير من زاويتك الهادية 🌸' },
      { en: 'May your day be soft and your tea be warm.', ar: 'اتمنى يومك يكون ناعم وشايك دافي.' },
      { en: 'Whatever today brings, you\'ve handled harder. Go shine ✨', ar: 'مهما صار اليوم، انتي عديتي اصعب منه، انطلقي ✨' }
    ],
    symptom_today: [
      { en: 'Thanks for telling me. Now go be extra gentle with yourself 💗', ar: 'شكرا انك سجلتيها، الحين روحي كوني لطيفة مع نفسك زيادة 💗' },
      { en: 'Noted with love. A warm shower and an early night sound nice ☁️', ar: 'سجلتها بكل حب، دش دافي ونومة بدري فكرة حلوة ☁️' },
      { en: 'Some days feel heavier. You don\'t have to carry them perfectly.', ar: 'بعض الايام اثقل من غيرها، مو لازم تشيلينها بشكل مثالي.' },
      { en: 'Logged. Now the only thing on your list is coziness 🫖', ar: 'تم التسجيل، الحين الشي الوحيد بقايمتك هو الراحة 🫖' },
      { en: 'I hear you. Wrap up warm and let today move slowly.', ar: 'سامعتك، تدفي وخلي اليوم يمشي على مهله.' },
      { en: 'You know yourself best. I\'m just here to keep you company 🌸', ar: 'انتي اعرف وحدة بنفسك، وانا هنا بس عشان اونسك 🌸' },
      { en: 'Extra softness ordered for you today, arriving right away ☁️', ar: 'طلبت لك جرعة لطف اضافية اليوم، وبتوصلك فورا ☁️' },
      { en: 'Be your own best friend today. You deserve that care 💗', ar: 'كوني افضل صديقة لنفسك اليوم، تستاهلين هذا الاهتمام 💗' }
    ]
  };

  // Current pick, module-scoped so re-renders in the same state keep the
  // same line on screen; only bloom-next (a tap on the mascot) advances.
  var bloomCurrent = null; // {bucket, index}

  // "Non-empty" symptoms entry: any checked symptom key or a flow value.
  function bloomSymptomsNonEmpty(s) {
    if (!s) return false;
    if (s.flow) return true;
    for (var i = 0; i < SYMPTOM_KEYS.length; i++) if (s[SYMPTOM_KEYS[i]]) return true;
    return false;
  }

  // Bucket priority: symptoms logged today > on period (day 1-2 gentle,
  // day 3+ encouraging) > period expected within 3 days > no data yet >
  // ordinary day. Open-cycle detection reuses findOpenCycleForEnd so
  // Bloom and the quick-log button can never disagree about "on period".
  function bloomBucket() {
    var todayStr = localTodayStr();
    if (bloomSymptomsNonEmpty(currentSymptoms()[todayStr])) return 'symptom_today';
    var cycles = currentCycles();
    var open = findOpenCycleForEnd(cycles, todayStr);
    if (open) {
      var day = Math.round((toDate(todayStr) - toDate(open.period_start)) / DAY) + 1;
      return day <= 2 ? 'period_early' : 'period_late';
    }
    var pred = currentPredictions();
    if (pred.daysUntil !== null && pred.daysUntil >= 0 && pred.daysUntil <= 3) return 'pre_period';
    if (!cycles.length) return 'welcome';
    return 'rest';
  }

  // Rotation memory: bw_bloom_seen = {bucket: [indices already shown]}.
  // Pick randomly among unseen indices; once a bucket is exhausted its
  // list resets. avoidIdx (the line currently on screen) is skipped when
  // possible so bloom-next always visibly changes the message. Storage
  // failures degrade to plain random, never to an error.
  function pickMessage(bucket, avoidIdx) {
    var msgs = BLOOM_LIB[bucket] || [];
    if (!msgs.length) return 0;
    var fallback = Math.floor(Math.random() * msgs.length);
    try {
      var seen = {};
      try {
        seen = JSON.parse(localStorage.getItem(BLOOM_SEEN_KEY) || '{}');
      } catch (e) { seen = {}; }
      if (!seen || typeof seen !== 'object') seen = {};
      var list = Array.isArray(seen[bucket]) ? seen[bucket] : [];
      var unseen = [], i;
      for (i = 0; i < msgs.length; i++) if (list.indexOf(i) === -1) unseen.push(i);
      if (!unseen.length) {
        list = [];
        for (i = 0; i < msgs.length; i++) unseen.push(i);
      }
      if (unseen.length > 1 && avoidIdx != null && avoidIdx >= 0) {
        var filtered = [];
        for (i = 0; i < unseen.length; i++) if (unseen[i] !== avoidIdx) filtered.push(unseen[i]);
        if (filtered.length) unseen = filtered;
      }
      var idx = unseen[Math.floor(Math.random() * unseen.length)];
      list.push(idx);
      seen[bucket] = list;
      try { localStorage.setItem(BLOOM_SEEN_KEY, JSON.stringify(seen)); } catch (e2) { /* storage full/blocked, rotation just forgets */ }
      return idx;
    } catch (e3) {
      return fallback;
    }
  }

  function bloomMessageText() {
    var msgs = BLOOM_LIB[bloomCurrent.bucket] || BLOOM_LIB.rest;
    var msg = msgs[bloomCurrent.index] || msgs[0];
    return LOCALE === 'ar' ? msg.ar : msg.en;
  }

  // Inline SVG mascot: 5 rounded petals around a soft cream face with
  // two eyes (CSS blink) and a tiny smile. Brand colors via the CSS
  // custom properties, nothing hardcoded here.
  function bloomSvg() {
    var petals = '';
    for (var i = 0; i < 5; i++) {
      petals += '<ellipse cx="32" cy="13" rx="9.5" ry="12" transform="rotate(' + (i * 72) + ' 32 32)"/>';
    }
    return '<svg class="bw-bloom-svg" viewBox="0 0 64 64" width="64" height="64" aria-hidden="true" focusable="false">' +
      '<g class="bw-bloom-petals">' + petals + '</g>' +
      '<circle class="bw-bloom-face" cx="32" cy="32" r="13"/>' +
      '<ellipse class="bw-bloom-eye" cx="27.5" cy="30.5" rx="1.6" ry="2.2"/>' +
      '<ellipse class="bw-bloom-eye" cx="36.5" cy="30.5" rx="1.6" ry="2.2"/>' +
      '<path class="bw-bloom-smile" d="M28 36 Q32 39.5 36 36"/>' +
      '</svg>';
  }

  function renderCompanion() {
    var bucket = bloomBucket();
    if (!bloomCurrent || bloomCurrent.bucket !== bucket) {
      bloomCurrent = { bucket: bucket, index: pickMessage(bucket) };
    }
    var h = '<div class="bw-bloom-card">';
    h += '<button type="button" class="bw-bloom-mascot" data-action="bloom-next" aria-label="' + esc(t('bloomAria')) + '">' + bloomSvg() + '</button>';
    h += '<div class="bw-bloom-body">';
    h += '<span class="bw-bloom-name">' + esc(t('bloomName')) + '</span>';
    h += '<div class="bw-bloom-bubble"><span class="bw-bloom-bubble-text">' + esc(bloomMessageText()) + '</span></div>';
    h += '</div>';
    h += '</div>';
    return h;
  }

  function renderCalendar(pred, cyclesList) {
    var symptomsMap = currentSymptoms();
    var grid = buildMonthGrid(state.viewYear, state.viewMonth);
    var h = '<section class="bw-t-cal">';
    h += '<div class="bw-t-cal-nav">';
    h += '<button type="button" class="bw-t-nav-btn" data-action="prev-month" aria-label="' + esc(t('prevMonth')) + '">‹</button>';
    h += '<span class="bw-t-cal-label">' + esc(t('monthNames')[state.viewMonth]) + ' ' + state.viewYear + '</span>';
    h += '<button type="button" class="bw-t-nav-btn" data-action="next-month" aria-label="' + esc(t('nextMonth')) + '">›</button>';
    h += '</div>';
    h += '<div class="bw-t-cal-grid bw-t-cal-weekdays">';
    for (var w = 0; w < 7; w++) h += '<span>' + esc(t('dayNames')[w]) + '</span>';
    h += '</div>';
    h += '<div class="bw-t-cal-grid">';
    for (var i = 0; i < grid.length; i++) {
      var cell = grid[i];
      if (!cell) { h += '<span class="bw-t-day bw-t-day--blank"></span>'; continue; }
      var dateStr = localDateStr(cell);
      var cls = classesForDate(dateStr, cyclesList, pred, symptomsMap);
      h += '<button type="button" class="bw-t-day ' + cls.join(' ') + '" data-action="open-day" data-date="' + dateStr + '">';
      h += '<span class="bw-t-day-num">' + cell.getDate() + '</span>';
      h += '</button>';
    }
    h += '</div>';
    h += '</section>';
    return h;
  }

  function renderFooter() {
    var h = '<footer class="bw-t-footer">';
    if (state.mode === 'server') {
      h += '<button type="button" class="bw-t-btn bw-t-btn--ghost" data-action="open-settings">' + esc(t('settings')) + '</button>';
    }
    h += '</footer>';
    return h;
  }

  function renderSheet() {
    if (!state.sheet) return '';
    var body = '';
    if (state.sheet.type === 'day') {
      body = renderDaySheet(state.sheet.date);
    } else if (state.sheet.type === 'symptoms') {
      body = renderSymptomsSheet(state.sheet.date);
    } else if (state.sheet.type === 'consent') {
      body = renderConsentSheet();
    } else if (state.sheet.type === 'settings') {
      body = renderSettingsSheet();
    }
    // NOTE: no data-action on the overlay, clicks inside the sheet on
    // elements without their own data-action (chips, inputs, text) bubble
    // up and closest('[data-action]') would match the backdrop, closing
    // the sheet. Backdrop-only closing is handled in onClick's no-match
    // branch via target.classList.contains('bw-t-overlay').
    return '<div class="bw-t-overlay">' +
      '<div class="bw-t-sheet" role="dialog" aria-modal="true">' + body + '</div>' +
      '</div>';
  }

  function renderDaySheet(dateStr) {
    var h = '<div class="bw-t-sheet-head"><h2>' + esc(fmtHuman(dateStr)) + '</h2>' +
      '<button type="button" class="bw-t-close" data-action="close-sheet" aria-label="' + esc(t('close')) + '">×</button></div>';
    h += '<div class="bw-t-sheet-actions">';
    h += '<button type="button" class="bw-t-btn bw-t-btn--primary bw-t-btn--block" data-action="log-start" data-date="' + dateStr + '">' + esc(t('logStart')) + '</button>';
    h += '<button type="button" class="bw-t-btn bw-t-btn--ghost bw-t-btn--block" data-action="log-end" data-date="' + dateStr + '">' + esc(t('logEnd')) + '</button>';
    h += '<button type="button" class="bw-t-btn bw-t-btn--ghost bw-t-btn--block" data-action="open-symptoms" data-date="' + dateStr + '">' + esc(t('logSymptomsAction')) + '</button>';
    h += '</div>';
    return h;
  }

  function renderSymptomsSheet(dateStr) {
    var saved = currentSymptoms()[dateStr] || {};
    var h = '<div class="bw-t-sheet-head"><h2>' + esc(t('symptoms')) + ', ' + esc(fmtHuman(dateStr)) + '</h2>' +
      '<button type="button" class="bw-t-close" data-action="close-sheet" aria-label="' + esc(t('close')) + '">×</button></div>';
    h += '<form class="bw-t-symptom-form" data-date="' + dateStr + '">';
    h += chipGroup('groupBody', ['cramps', 'backache', 'breast_tenderness', 'bloating', 'headache'], saved);
    h += chipGroup('groupMood', ['mood', 'fatigue'], saved);
    h += chipGroup('groupSkin', ['skin', 'cravings', 'sleep'], saved);
    h += '<div class="bw-t-flow">';
    h += '<span class="bw-t-flow-label">' + esc(t('flow')) + '</span>';
    h += '<div class="bw-t-chip-row">';
    h += flowChip('light', t('flowLight'), saved.flow);
    h += flowChip('medium', t('flowMedium'), saved.flow);
    h += flowChip('heavy', t('flowHeavy'), saved.flow);
    h += '</div></div>';
    h += '<button type="submit" class="bw-t-btn bw-t-btn--primary bw-t-btn--block">' + esc(t('save')) + '</button>';
    h += '</form>';
    return h;
  }
  function chipGroup(labelKey, keys, saved) {
    var h = '<p class="bw-t-chip-group-label">' + esc(t(labelKey)) + '</p><div class="bw-t-chip-row">';
    for (var i = 0; i < keys.length; i++) h += chip(keys[i], t('sym_' + keys[i]), saved[keys[i]]);
    return h + '</div>';
  }
  function chip(key, label, checked) {
    return '<label class="bw-t-chip"><input type="checkbox" name="' + key + '"' + (checked ? ' checked' : '') + '><span>' + esc(label) + '</span></label>';
  }
  function flowChip(val, label, savedFlow) {
    return '<label class="bw-t-chip"><input type="radio" name="flow" value="' + val + '"' + (savedFlow === val ? ' checked' : '') + '><span>' + esc(label) + '</span></label>';
  }

  function renderConsentSheet() {
    var h = '<div class="bw-t-sheet-head"><h2>' + esc(t('consentTitle')) + '</h2>' +
      '<button type="button" class="bw-t-close" data-action="close-sheet" aria-label="' + esc(t('close')) + '">×</button></div>';
    h += '<p class="bw-t-consent-body">' + esc(t('consentBody')) + '</p>';
    h += '<label class="bw-t-chip bw-t-chip--wide"><input type="checkbox" id="bw-t-reminders-opt"><span>' + esc(t('consentReminders')) + '</span></label>';
    h += '<div class="bw-t-sheet-actions">';
    h += '<button type="button" class="bw-t-btn bw-t-btn--primary bw-t-btn--block" data-action="accept-consent">' + esc(t('consentAccept')) + '</button>';
    h += '<button type="button" class="bw-t-btn bw-t-btn--ghost bw-t-btn--block" data-action="close-sheet">' + esc(t('consentCancel')) + '</button>';
    h += '</div>';
    return h;
  }

  function renderSettingsSheet() {
    var pred = currentPredictions();
    var user = state.server && state.server.user ? state.server.user : {};
    var h = '<div class="bw-t-sheet-head"><h2>' + esc(t('settings')) + '</h2>' +
      '<button type="button" class="bw-t-close" data-action="close-sheet" aria-label="' + esc(t('close')) + '">×</button></div>';
    h += '<label class="bw-t-field"><span>' + esc(t('cycleLength')) + '</span>' +
      '<input type="number" min="21" max="45" id="bw-t-cycle-len" value="' + esc(user.avg_cycle_len || pred.avgCycle) + '"></label>';
    h += '<label class="bw-t-chip bw-t-chip--wide"><input type="checkbox" id="bw-t-reminders-toggle"' + (user.reminders_enabled ? ' checked' : '') + '><span>' + esc(t('reminders')) + '</span></label>';
    // v3.1 whatsapp option: lives in the same server-gated sheet as the
    // email reminders row above (Settings only opens in server mode, see
    // renderFooter). Toggling ON only reveals the phone input via class
    // (no re-render, so a typed number survives); the API call happens on
    // the save button, or immediately on toggle OFF when the server has
    // it enabled (which also deletes the stored number server-side).
    h += '<div class="bw-t-wa">';
    h += '<label class="bw-t-chip bw-t-chip--wide bw-t-wa-row"><input type="checkbox" id="bw-t-wa-toggle"' + (user.wa_enabled ? ' checked' : '') + '><span>' + esc(t('waReminders')) + '</span><span class="bw-t-wa-badge">' + esc(t('waSoon')) + '</span></label>';
    h += '<div id="bw-t-wa-detail" class="bw-t-wa-detail' + (user.wa_enabled ? ' is-open' : '') + '">';
    h += '<input type="tel" id="bw-t-wa-phone" dir="ltr" inputmode="tel" placeholder="05XXXXXXXX" value="' + esc(user.wa_phone || '') + '">';
    h += '<button type="button" class="bw-t-btn bw-t-btn--primary bw-t-btn--block" data-action="save-wa">' + esc(t('waSave')) + '</button>';
    h += '</div>';
    h += '<p class="bw-t-wa-micro">' + esc(t('waMicro')) + '</p>';
    h += '</div>';
    h += '<div class="bw-t-sheet-actions">';
    h += '<button type="button" class="bw-t-btn bw-t-btn--primary bw-t-btn--block" data-action="save-cycle-length">' + esc(t('save')) + '</button>';
    h += '<button type="button" class="bw-t-btn bw-t-btn--ghost bw-t-btn--block" data-action="download-data">' + esc(t('downloadData')) + '</button>';
    h += '<button type="button" class="bw-t-btn bw-t-btn--danger bw-t-btn--block" data-action="delete-data">' + esc(t('deleteData')) + '</button>';
    h += '</div>';
    return h;
  }

  function openSheet(descriptor) { state.sheet = descriptor; render(); }
  function closeSheet() { state.sheet = null; render(); }

  /* ------------------------------------------------------------------ *
   * Events (delegated, root re-rendered on every state change)
   * ------------------------------------------------------------------ */
  function bindEvents() {
    ROOT.addEventListener('click', onClick);
    var symptomForm = ROOT.querySelector('.bw-t-symptom-form');
    if (symptomForm) symptomForm.addEventListener('submit', onSymptomSubmit);
    var remindersToggle = ROOT.querySelector('#bw-t-reminders-toggle');
    if (remindersToggle) remindersToggle.addEventListener('change', function () {
      saveSetting({ reminders_enabled: remindersToggle.checked });
    });
    // v3.1 whatsapp option: ON just reveals the input (no API call, a phone
    // number is required first); OFF calls the API only when the server
    // currently has it enabled (that call also deletes the stored number).
    var waToggle = ROOT.querySelector('#bw-t-wa-toggle');
    if (waToggle) waToggle.addEventListener('change', function () {
      var waDetail = ROOT.querySelector('#bw-t-wa-detail');
      var waUser = state.server && state.server.user ? state.server.user : {};
      if (waToggle.checked) {
        if (waDetail) waDetail.classList.add('is-open');
      } else if (waUser.wa_enabled) {
        saveSetting({ wa_enabled: false });
      } else {
        if (waDetail) waDetail.classList.remove('is-open');
      }
    });
  }

  function onClick(evt) {
    var el = evt.target.closest('[data-action]');
    if (!el) {
      if (evt.target.classList && evt.target.classList.contains('bw-t-overlay')) closeSheet();
      return;
    }
    var action = el.getAttribute('data-action');
    switch (action) {
      case 'prev-month':
        state.viewMonth--;
        if (state.viewMonth < 0) { state.viewMonth = 11; state.viewYear--; }
        render();
        break;
      case 'next-month':
        state.viewMonth++;
        if (state.viewMonth > 11) { state.viewMonth = 0; state.viewYear++; }
        render();
        break;
      case 'view-calendar':
        state.view = 'calendar'; render(); break;
      case 'view-mycycle':
        state.view = 'mycycle'; render(); break;
      case 'open-day':
        openSheet({ type: 'day', date: el.getAttribute('data-date') });
        break;
      case 'open-symptoms':
        openSheet({ type: 'symptoms', date: el.getAttribute('data-date') });
        break;
      case 'log-start':
        addCycleStart(el.getAttribute('data-date'));
        closeSheet();
        break;
      case 'log-end':
        endCycle(el.getAttribute('data-date'));
        closeSheet();
        break;
      // v3 quick log (Task 2): one-tap today shortcuts. Same mutations as
      // log-start/log-end above (they own the toast + re-render + server
      // error fallback internally); no sheet is open, so no closeSheet().
      case 'quick-start':
        addCycleStart(localTodayStr());
        break;
      case 'quick-end':
        endCycle(localTodayStr());
        break;
      // v3.2 cycle history: reveal the rows past the first 6. The flag is a
      // module var, so later re-renders keep the list expanded.
      case 'history-more':
        historyOpen = true;
        render();
        break;
      // v3 bloom companion (Task 3): tap the mascot for the next line.
      // Updates the bubble text in place (no full re-render, so nothing
      // else on the page flickers) and re-triggers the bounce by class
      // removal + reflow + re-add.
      case 'bloom-next':
        var bnBucket = bloomBucket();
        var bnAvoid = (bloomCurrent && bloomCurrent.bucket === bnBucket) ? bloomCurrent.index : -1;
        bloomCurrent = { bucket: bnBucket, index: pickMessage(bnBucket, bnAvoid) };
        var bnBubble = ROOT.querySelector('.bw-bloom-bubble-text');
        if (bnBubble) bnBubble.textContent = bloomMessageText();
        var bnMascot = ROOT.querySelector('.bw-bloom-mascot');
        if (bnMascot) {
          bnMascot.classList.remove('bw-bloom-bounce');
          void bnMascot.offsetWidth; // force reflow so the animation restarts
          bnMascot.classList.add('bw-bloom-bounce');
        }
        break;
      case 'open-consent':
        openSheet({ type: 'consent' });
        break;
      case 'accept-consent':
        if (inFlight.consent) break;
        inFlight.consent = true;
        el.disabled = true;
        var remindersEl = document.getElementById('bw-t-reminders-opt');
        submitConsent(remindersEl && remindersEl.checked);
        break;
      case 'open-settings':
        openSheet({ type: 'settings' });
        break;
      case 'save-cycle-length':
        if (inFlight.cycleLength) break;
        var lenEl = document.getElementById('bw-t-cycle-len');
        var n = parseInt(lenEl && lenEl.value, 10);
        if (isNaN(n)) break;
        n = Math.min(45, Math.max(21, n));
        inFlight.cycleLength = true;
        el.disabled = true;
        saveSetting({ avg_cycle_len: n }).then(function () { inFlight.cycleLength = false; });
        break;
      // v3.1 whatsapp option: client-side sanity check, then the normal
      // saveSetting path (success = refreshServer + render with saved
      // state; errors are toasted inside saveSetting).
      case 'save-wa':
        if (inFlight.wa) break;
        var waPhoneEl = document.getElementById('bw-t-wa-phone');
        var waPhone = waPhoneEl ? waPhoneEl.value.replace(/^\s+|\s+$/g, '') : '';
        if (!waPhone || !/^[0-9+][0-9 ()-]{6,18}$/.test(waPhone)) { toast(t('waInvalid')); break; }
        inFlight.wa = true;
        el.disabled = true;
        saveSetting({ wa_enabled: true, wa_phone: waPhone }).then(function () { inFlight.wa = false; });
        break;
      case 'download-data':
        downloadData();
        break;
      case 'delete-data':
        deleteAllData();
        break;
      case 'close-sheet':
      case 'close-overlay':
        closeSheet();
        break;
    }
  }

  function onSymptomSubmit(evt) {
    evt.preventDefault();
    if (inFlight.symptoms) return;
    var form = evt.target;
    var submitBtn = form.querySelector('button[type="submit"]');
    var dateStr = form.getAttribute('data-date');
    var symptoms = {};
    for (var si = 0; si < SYMPTOM_KEYS.length; si++) {
      var sk = SYMPTOM_KEYS[si], sEl = form.querySelector('[name="' + sk + '"]');
      symptoms[sk] = !!(sEl && sEl.checked);
    }
    symptoms.flow = (function () { var r = form.querySelector('[name="flow"]:checked'); return r ? r.value : null; })();
    inFlight.symptoms = true;
    if (submitBtn) submitBtn.disabled = true;
    logSymptoms(dateStr, symptoms).then(function () { inFlight.symptoms = false; });
    closeSheet();
  }

  /* ------------------------------------------------------------------ *
   * v3 layout: desktop breakpoint (Task 1). Desktop renders calendar
   * and My Cycle side by side and skips the tab bar; mobile keeps the
   * tab swap. One change listener, registered once at init, re-renders
   * when the viewport crosses the breakpoint.
   * ------------------------------------------------------------------ */
  function isDesktop() {
    return window.matchMedia('(min-width:900px)').matches;
  }
  /* ------------------------------------------------------------------ *
   * v3 quick log (Task 2): pill visibility observer. render() wipes the
   * whole ROOT subtree, so the observed hero node dies on every render;
   * this disconnects the old observer and observes the fresh node each
   * time. Without IntersectionObserver support the pill simply never
   * gains is-visible (it starts hidden by CSS).
   * ------------------------------------------------------------------ */
  var quickPillIO = null;
  function refreshQuickPillObserver() {
    if (quickPillIO) { quickPillIO.disconnect(); quickPillIO = null; }
    if (typeof window.IntersectionObserver === 'undefined') return;
    var target = ROOT.querySelector('.bw-t-quick-row') || ROOT.querySelector('.bw-t-area-hero');
    if (!target || !ROOT.querySelector('.bw-t-quickpill')) return;
    quickPillIO = new IntersectionObserver(function (entries) {
      var pill = ROOT.querySelector('.bw-t-quickpill');
      if (!pill) return;
      var rowOut = entries.length && !entries[entries.length - 1].isIntersecting;
      if (rowOut && !state.sheet && !isDesktop()) pill.classList.add('is-visible');
      else pill.classList.remove('is-visible');
    }, { threshold: 0 });
    quickPillIO.observe(target);
  }

  var mediaBound = false;
  function bindMediaListener() {
    if (mediaBound) return; // guard, never register twice
    mediaBound = true;
    var mql = window.matchMedia('(min-width:900px)');
    var onChange = function () { render(); };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange); // older Safari
  }

  bindMediaListener();
  ensureToast();
  boot();
})();
